"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createHttpClient = exports.encodeQueryParams = void 0;
// TODO: nets is unmaintained. Move to something else.
// https://nerdwallet.atlassian.net/browse/FEI-2443
const nets_1 = __importDefault(require("nets"));
const lodash_1 = __importDefault(require("lodash"));
const datadog_1 = __importDefault(require("@nerdwallet/datadog"));
const headers_1 = require("./headers");
/**
 * Encode a params object to URL query string parameters.
 *
 * In the future, this code may not need to exist anymore. For instance, Axios
 * will encode query params on our behalf. Or, we could switch to using
 * `new URLSearchParams(...).toString()`. Note that these will encode things
 * differently than they have been historically, e.g. a space will be encoded
 * as `+` instead of `%20`. Our backends should be tolerant of this, but it is
 * a risk that should be evaluated before changing this behavior.
 *
 * @see https://stackoverflow.com/questions/59889140/different-output-from-encodeuricomponent-vs-urlsearchparams
 * @see https://github.com/axios/axios/blob/d99d5faac29899eba68ce671e6b3cbc9832e9ad8/lib/helpers/buildURL.js#L5
 */
function encodeQueryParams(params) {
    return Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
}
exports.encodeQueryParams = encodeQueryParams;
// TODO: there must be a better way to do this. I fear that using the Buffer
// global here will cause webpack or Babel to include a Buffer polyfill.
//
// TODO: the best opportunity to revamp or remove this function will probably
// be when we decide to replace `nets` with something else.
// https://nerdwallet.atlassian.net/browse/FEI-2443
function didFailToParse(body) {
    // In the case of node.js it leaves the data object as a utf8 encoded Buffer object.
    if (typeof Buffer !== 'undefined') {
        return body instanceof Buffer || typeof body === 'string';
    }
    // In the case of the browser, the request module will leave the body as a string.
    // (assuming the config.json option is set)
    return typeof body === 'string';
}
function getNWA() {
    if (typeof window !== 'undefined') {
        return window.nwa;
    }
    return undefined;
}
/**
 * Wrap `nets` so that it returns a Promise.
 */
function netsPromisified(configNets, config) {
    return new Promise((resolve, reject) => {
        configNets(config, (err, response, body) => {
            if (err) {
                // Attach response and body to the err so caller can see the response
                // eslint-disable-next-line no-param-reassign
                err.response = response;
                // eslint-disable-next-line no-param-reassign
                err.body = body;
                reject(err);
            }
            else {
                resolve({ response, body });
            }
        });
    });
}
/**
 * Creates a new `HttpClient` instance, which can be used to make calls to
 * nwapi services with the correct headers, etc.
 */
function createNewHttpClient(config) {
    // Timeout is specified in milliseconds.
    const DEFAULT_TIMEOUT = 5000;
    const { nets: configNets, nwa, xhrTimeout } = config;
    let kountSessionID;
    let referringClientID;
    function setKountSessionID(id) {
        kountSessionID = id;
    }
    function setReferringClientID(id) {
        referringClientID = id;
    }
    function setUserLoggedIn() {
        if (nwa) {
            nwa.setUserProp('gaUserAuthenticationState', 'logged_in');
        }
    }
    function setUserLoggedOut() {
        if (nwa) {
            nwa.setUserProp('gaUserAuthenticationState', 'logged_out');
        }
    }
    function fetch(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { body, canonicalPath, followRedirect, method, serverRequest, url } = args;
            let { json } = args;
            const headers = args.headers || {};
            const cookies = args.cookies || {};
            const params = args.params || {};
            const timeout = args.timeout || xhrTimeout || DEFAULT_TIMEOUT;
            let queryParams = encodeQueryParams(params);
            // Only tack on question mark if query params are present
            queryParams = queryParams ? `?${queryParams}` : '';
            // For GET/HEAD requests, use `json: true`, which instructs
            // nets/requests/xhr to expect a JSON response but does not send a request
            // body. For other requests, set `json: {}`, which will also default to a
            // empty object as the request body. This is because nw_api requires a
            // valid json request body even if there are no inputs. Ideally SDKs would
            // should explicitly pass in {} as input when this is needed, but too many
            // existing SDKs rely on the behavior here.
            if (typeof body === 'undefined' && typeof json === 'undefined') {
                if (method === 'GET' || method === 'HEAD') {
                    json = true;
                }
                else {
                    json = {};
                }
            }
            // Basic request config.
            const netsConfig = {
                method,
                url: url + queryParams,
                followRedirect,
                timeout,
                withCredentials: true,
                body,
            };
            if (typeof json !== 'undefined') {
                netsConfig.json = json;
            }
            const context = {
                url,
                config,
                serverRequest,
                cookies,
                referringClientID,
                kountSessionID,
            };
            // Get all headers, including cookies
            const coreHeaders = yield (0, headers_1.getCoreHTTPHeaders)(context);
            netsConfig.headers = Object.assign(Object.assign({}, headers), coreHeaders);
            const { response, body: responseBody } = yield (!canonicalPath
                ? netsPromisified(configNets, netsConfig)
                : datadog_1.default.instrumented('node.outgoing_requests.http', {
                    tags: {
                        caller_client_id: config.clientID,
                        domain: url.replace(/^https?:\/\//, '').split('/')[0],
                        endpoint: canonicalPath.replace(/^https?:\/\//, ''),
                        method,
                    },
                }, () => netsPromisified(configNets, netsConfig)));
            // 401 sometimes implies that a user is unauthenticated and we need to update our tracking accordingly
            // TODO: Revisit when we have error codes (PIE-1108)
            if (response &&
                response.statusCode === 401 &&
                lodash_1.default.get(responseBody, 'error.data.user_is_logged_out')) {
                setUserLoggedOut();
            }
            // The underlying 'request' (github.com/request/request) node module will attempt to
            // JSON.parse the response body, but won't do anything if it fails to parse.
            // If the response contains a Content-Type header of application/json, but request failed to
            // parse the result into an object, then we can assume that this is unexpected and should
            // therefore reject with an error
            // Lee: added a check to ignore HTTP 204: No content as... it's no content.
            if (response &&
                response.headers['content-type'] === 'application/json' &&
                response.statusCode !== 204 &&
                didFailToParse(responseBody)) {
                const parseError = new Error('Failed to JSON.parse body');
                // TODO: separate type?
                parseError.response = response;
                parseError.body = responseBody;
                throw parseError;
            }
            return { response, body: responseBody };
        });
    }
    return {
        fetch,
        setKountSessionID,
        setReferringClientID,
        setUserLoggedIn,
        setUserLoggedOut,
    };
}
function createHttpClient(config) {
    if (!config || typeof config !== 'object') {
        throw new Error('config is required');
    }
    return createNewHttpClient(Object.assign({ nets: nets_1.default, nwa: getNWA() }, config));
}
exports.createHttpClient = createHttpClient;
